// Waste area shell (office, Module 05 §4 — slice 05-B). Sub-navigation over
// the slice's three data screens plus 05-A's settings editor. Registered at
// /waste; the index redirects to /waste/events — the ledger is the screen that
// answers a question without an id in hand.
//
// WHAT IS DELIBERATELY ABSENT (D-033 / spec 05 §4 slice ownership): S1 command
// board, S3 variance, S6 matches, S7 PVC, S10 sales — no link, because none of
// those screens exists yet (05-C/05-D own them). A link to a screen with no
// data behind it is a disabled shell.
//
// Nav gating mirrors §7 (fail-closed, hooks/permissions.ts): the ledger and
// shift scoreboard ride `waste.read` (S2/S4's role sets are exactly its holder
// set); the remnant registry rides `waste.remnant.manage` (S5's storekeeper /
// sales / planner audience holds no `waste.read` — the registry is their
// surface, not the ledger); settings keeps 05-A's read-roles rule.
//
// UX-4: the sub-navigation is the §3.10 `RouterTabs` primitive — URL-backed
// links with `aria-current`, underlined in the accent, WRAPPING (never
// scrolling) at ≤768 px. It used to be four bare hyperlinks wearing the
// CATALOGUE's `.catalogue-subnav-link` class inside a `.catalogue-content`
// wrapper: both the ad-hoc-tab markup gate check 3a ratchets away AND a
// cross-area style dependency — module 05's navigation was rendered by module
// 01's stylesheet, and would have silently lost its styling the day the
// catalogue renamed a class. (The identical defect module 04 removed at UX-3;
// this file is the last office copy of it.)
//
// RENDER-ONCE (§5), AND THE WHOLE AREA AT ONCE. All four entries are
// permission-decided, so the strip's WIDTH — and at 412 px its HEIGHT, since it
// wraps — is decided by a role feed that arrives after first paint. Painting
// the strip before the mirror settles drops the page contents by a wrapped row
// when the last entry resolves (the CLS 0.0476 UX-2 measured on the catalogue,
// and the 0.0004 UX-1 measured on onboarding). While the mirror is outstanding
// the area is a reserving skeleton whose box IS `.ds-tabs`'s pinned min-height;
// when it settles the whole subtree REMOUNTS (`key` swap) so nothing painted
// early ever moves.

import { useEffect } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton, ForbiddenState } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { useWastePermissions, wasteAreaEntries, wasteLandingPath } from "./hooks/permissions";
import "./waste.css";

export function WasteLayout() {
  const { t } = useTranslation();
  const perms = useWastePermissions();

  // ONE ENTRY TABLE (hooks/permissions.ts). The strip used to hold its own copy
  // of the gating rule while routes/root.tsx held a THIRD, narrower one — which
  // is how five roles holding `waste.*` ended up with no link into the area.
  const tabs: RouterTabDef[] = wasteAreaEntries(perms).map((entry) => ({
    to: entry.to,
    label: t(entry.labelKey),
  }));

  if (!perms.isResolved) {
    return (
      <div className="waste-area" key="waste-loading" data-testid="waste-loading">
        <div className="ds-tabs waste-subnav-skel" aria-hidden="true" />
        <div className="waste-body">
          <CardSkeleton lines={6} label={t("waste.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="waste-area" key="waste-ready">
      <RouterTabs tabs={tabs} label={t("waste.nav.aria")} data-testid="waste-subnav" />
      <div className="waste-body">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * `/waste` — land this principal on the first screen they can actually open.
 *
 * It used to be a `beforeLoad` redirect to `/waste/events`, unconditionally. A
 * `beforeLoad` runs outside React and cannot read the role feed, so a
 * storekeeper — who holds `waste.remnant.manage` and NOT `waste.read` — was
 * sent to the one screen in the area they are refused. It is a component so it
 * can wait for the mirror and then send them to the registry that was built for
 * them. The parent shell has already reserved the layout, so this renders
 * nothing while it decides.
 */
export function WasteIndexRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useWastePermissions();
  const to = wasteLandingPath(perms);

  useEffect(() => {
    if (to !== undefined) void navigate({ to, replace: true });
  }, [to, navigate]);

  if (to !== undefined) return null;
  return <ForbiddenState title={t("waste.access.title")} body={t("waste.access.area")} />;
}
